/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { capitalize } from '@ember/string';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { formatCurrency } from 'cio/helpers/format-currency';
import { formatNumber } from 'cio/helpers/format-number';
import AccountModel from 'cio/models/account';
import MomentService from 'cio/services/-moment';
import AnalyticsService from 'cio/services/analytics';
import BillingService, { BASIC_2018_PLAN_SLUG } from 'cio/services/billing';
import { DataPipelinesPlanTypes, Plans } from 'cio/utils/billing/plans';
import { compactNumber } from 'cio/utils/formatters';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import FeaturesService from 'ember-feature-flags/services/features';
import getCurrentPlan from 'cio/utils/billing/current-plan';
import SidebarService, { StaticSidebarId } from 'cio/services/sidebar';
import AccountService from 'cio/services/account';
import { ProductApp } from 'cio/utils/product-app-info';

interface CurrentPlansArgs {
  account: AccountModel;
  showCancelModal: () => void;
}

export type BannerId = 'multi' | 'messages' | 'profiles' | 'api-calls';

interface Banner {
  id: BannerId;
  type: 'limit-exceeded';
  highlight: string;
  text: string;
}

function getPercentage(value: number, total: number) {
  if (total === 0) {
    return 0;
  }
  if (total < value) {
    return 100;
  }
  return Math.round((value / total) * 100);
}

function getMeteredRateValue(meteredRate: string | undefined | null) {
  if (meteredRate == null) {
    return '0';
  }
  if (meteredRate === 'enterprise') {
    // Some legacy plans use "enterprise" as value for meteredCustomerRate
    return meteredRate;
  }

  let meteredRateFloat = parseFloat(meteredRate);
  if (!isNaN(meteredRateFloat)) {
    return meteredRateFloat;
  }
  return '0';
}

export default class CurrentPlans extends Component<CurrentPlansArgs> {
  @service account!: AccountService;
  @service moment!: MomentService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service billing!: BillingService;
  @service sidebar!: SidebarService;

  paperAirplaneImage = new URL('cio/assets/images/paper-airplane.png', import.meta.url);
  databaseImage = new URL('cio/assets/images/database.png', import.meta.url);

  readonly sidebarId = StaticSidebarId.CurrentPlans;

  constructor(owner: unknown, args: CurrentPlansArgs) {
    super(owner, args);
    this.refreshAccountUsageTask.perform();
  }

  refreshAccountUsageTask = task(async () => {
    await this.store.queryRecord('account-usage', {});
  });

  get hasMessagingPlan() {
    // All plans are journeys unless it is a CDP Only plan
    // CDP Only plans returns either cdp-premium or cdp-starter
    return (
      (this.billing.subscription?.plan.planType !== Plans.CdpPremium.planType &&
        this.billing.subscription?.plan.planType !== Plans.CdpStarter.planType) ||
      this.account?.planFeatures?.messaging?.enabled === true
    );
  }

  get hasCdpPlan() {
    let planType = this.billing.subscription?.plan.planType;
    if (planType != null) {
      return DataPipelinesPlanTypes.includes(planType) ?? false;
    }
    return false;
  }

  get hasAvailablePlans() {
    return !this.isJourneysActivated || !this.isDataPipelinesActivated || !this.hasMessagingPlan;
  }

  get usageMetrics() {
    let usage = this.args.account.get('usage');

    // account.usage can be null when account is cancelled
    // it's an async relationship and so should always be at least a proxy, so
    // looks like Ember Data bug? Anyway we have to protect against it:
    // test: http://127.0.0.1:4200/tests?testId=42920f01
    if (usage == null) {
      return {
        customersAllotted: 0,
        objectsAllotted: 0,
        messagesAllotted: 0,
        totalCustomerCount: 0,
        totalObjectCount: 0,
        messagesSent: 0,
        billableMessagesSent: 0,
        highWatermarkCustomersTotal: 0,
        highWatermarkObjectsTotal: 0,
        cdpApiCalls: 0,
        cdpApiCallsAllotted: 0,
        cdpApiCurrentTierRate: 0,
      };
    }

    return usage.getProperties(
      'customersAllotted',
      'objectsAllotted',
      'messagesAllotted',
      'totalCustomerCount',
      'totalObjectCount',
      'messagesSent',
      'billableMessagesSent',
      'highWatermarkCustomersTotal',
      'highWatermarkObjectsTotal',
      'cdpApiCalls',
      'cdpApiCallsAllotted',
      'cdpApiCurrentTierRate',
    );
  }

  get ProductAppEnum() {
    return ProductApp;
  }

  get isJourneysActivated() {
    return this.account.current?.productActivations?.includes?.(ProductApp.JOURNEYS) ?? false;
  }

  get isDataPipelinesActivated() {
    return this.account.current?.productActivations?.includes?.(ProductApp.DATA_PIPELINES) ?? false;
  }

  get messagingDescriptionItems() {
    let { customersAllotted, messagesAllotted, objectsAllotted } = this.usageMetrics;
    let { billing } = this;
    let basePrice =
      billing.subscription?.annual === true ? billing.subscription.plan.annualPrice : billing.subscription?.plan.price;
    let meteredCustomerRate = getMeteredRateValue(billing.subscription?.plan.meteredCustomerRate);
    let meteredObjectRate = getMeteredRateValue(billing.subscription?.plan.meteredObjectRate);

    // Plan details for non trial plans and for Journeys Trial on Pipelines plan
    let planDetails = {
      planName: this.isJourneysTrialOnPipelinesPlan ? 'Journeys Trial' : billing.planNameShortened,
      basePrice: this.isJourneysTrialOnPipelinesPlan ? null : basePrice,
      profilesAllotted: customersAllotted,
      objectProfilesAllotted: objectsAllotted,
      details:
        meteredCustomerRate === 'enterprise' ? "Add'l costs will apply" : `$${meteredCustomerRate} per add'l contact`,
      objectsDetails:
        meteredObjectRate === 'enterprise' ? "Add'l costs will apply" : `$${meteredObjectRate} per add'l object`,
    };

    // Plan details for Essentials and Premium trial plans
    if (billing.billingInfo?.trial === true && billing.billingInfo?.premiumTrial !== true) {
      planDetails = {
        planName: billing.planNameShortened,
        basePrice: Plans.Essentials.basePrice,
        profilesAllotted: Plans.Essentials.baseCustomerCount,
        objectProfilesAllotted: Plans.Essentials.baseObjectCount as number,
        details: `$${Plans.Essentials.baseMeteredRate} per add'l contact`,
        objectsDetails: `$${Plans.Essentials.baseMeteredObjectRate} per add'l object`,
      };
    }

    return [
      {
        id: 'plan',
        title: 'Plan',
        value: planDetails.planName,
        detailsLink: {
          text: 'View plan benefits',
          url: 'https://customer.io/pricing/',
        },
        width: 20,
      },
      ...(planDetails.basePrice != null
        ? [
            {
              id: 'base-price',
              title: 'Base Price',
              value: formatCurrency([planDetails.basePrice], { precision: 0 }),
              details: `${capitalize(billing.subscription?.plan.intervalNameForDisplay as string)} billing`,
              width: 14,
            },
          ]
        : []),
      {
        id: 'included-profiles',
        title: 'Included Contacts',
        value: `${compactNumber(planDetails.profilesAllotted)} per month`,
        details: planDetails.details,
        width: 22,
      },
      {
        id: 'included-object-profiles',
        title: 'Included Objects',
        value: `${compactNumber(planDetails.objectProfilesAllotted)} per month`,
        details: planDetails.objectsDetails,
        width: 22,
      },
      {
        id: 'included-email-sends',
        title: 'Included Email Sends',
        value: `${compactNumber(messagesAllotted)} per month`,
        details: "$0.00012 per add'l send",
        width: 24,
        tooltip: {
          text: 'Your plan includes unlimited in-app messages and push notifications.',
          tooltipLink: {
            text: 'Learn more',
            url: 'https://customer.io/docs/how-we-bill/',
          },
        },
      },
    ];
  }

  get messagingUsageItems() {
    let { billing } = this;
    let { account } = this.args;
    let { customersAllotted, messagesAllotted, billableMessagesSent, objectsAllotted } = this.usageMetrics;
    let isFreeTrial = billing.billingInfo?.trial === true && billing.billingInfo?.premiumTrial !== true;
    let profilesAllotted = isFreeTrial ? Plans.Essentials.baseCustomerCount : customersAllotted;
    let profilesCount =
      account.planIncludeChurn === true
        ? this.usageMetrics.highWatermarkCustomersTotal
        : this.usageMetrics.totalCustomerCount;
    let objectProfilesAllotted = isFreeTrial ? Plans.Essentials.baseObjectCount : objectsAllotted;
    let objectsCount =
      account.planIncludeChurn === true
        ? this.usageMetrics.highWatermarkObjectsTotal
        : this.usageMetrics.totalObjectCount;
    return [
      {
        id: 'profiles',
        title: 'People Profiles',
        details: `${formatNumber([profilesCount])} of ${formatNumber([profilesAllotted])}`,
        percentage: getPercentage(profilesCount, profilesAllotted),
      },
      {
        id: 'objects',
        title: 'Object Profiles',
        details: `${formatNumber([objectsCount])} of ${formatNumber([objectProfilesAllotted])}`,
        percentage: getPercentage(objectsCount, objectProfilesAllotted),
      },
      {
        id: 'emails',
        title: 'Emails sent',
        description: `Usage resets on ${this.moment.formatTimestamp([account.planPeriodEnd], {
          finalFormatter: (timestampAsMoment: moment.Moment) => timestampAsMoment.format('MMM D, YYYY'),
        })}`,
        details: `${formatNumber([billableMessagesSent])} of ${formatNumber([messagesAllotted])}`,
        percentage: getPercentage(billableMessagesSent, messagesAllotted),
      },
    ];
  }

  get messagingBanner(): Banner | undefined {
    let { billing } = this;
    let { account } = this.args;
    if (billing.billingInfo?.trial === false) {
      let {
        messagesAllotted,
        customersAllotted,
        objectsAllotted,
        billableMessagesSent,
        totalCustomerCount,
        totalObjectCount,
        highWatermarkCustomersTotal,
        highWatermarkObjectsTotal,
      } = this.usageMetrics;
      let profilesCount = account.planIncludeChurn ? highWatermarkCustomersTotal : totalCustomerCount;
      let objectProfilesCount = account.planIncludeChurn ? highWatermarkObjectsTotal : totalObjectCount;
      let billEstimate = formatCurrency([billing.billingInfo.estimatedInvoiceCost], { precision: 0 });

      let messagesExceeded = messagesAllotted < billableMessagesSent;
      let profilesExceeded = customersAllotted > 0 && customersAllotted < profilesCount;
      let objectProfilesExceeded = objectsAllotted > 0 && objectsAllotted < objectProfilesCount;
      let exceededLimits = [];
      if (profilesExceeded) {
        exceededLimits.push('more people profiles');
      }
      if (objectProfilesExceeded) {
        exceededLimits.push('more object profiles');
      }
      if (messagesExceeded) {
        exceededLimits.push('sent more emails');
      }

      if (exceededLimits.length > 1) {
        return {
          id: 'multi',
          type: 'limit-exceeded',
          highlight: `You have ${exceededLimits.join(' and ')} than are included in your plan.`,
          text: `Additional usage cost will be added to your next bill, which we currently estimate to be ${billEstimate}.`,
        };
      }
      if (messagesExceeded) {
        return {
          id: 'messages',
          type: 'limit-exceeded',
          highlight: `You sent more emails than are included in your plan.`,
          text: `Additional emails will be charged at $0.00012 and will be added to your next bill, which we currently estimate to be ${billEstimate}.`,
        };
      }
      if (profilesExceeded) {
        let meteredRateCustomerValue = getMeteredRateValue(billing.subscription?.plan.meteredCustomerRate);
        let text =
          meteredRateCustomerValue === 'enterprise'
            ? `Additional people profiles will have additional cost and will be added to your next bill, which we currently estimate to be ${billEstimate}.`
            : `Additional people profiles will be charged at $${meteredRateCustomerValue} and will be added to your next bill, which we currently estimate to be ${billEstimate}.`;
        return {
          id: 'profiles',
          type: 'limit-exceeded',
          highlight: `You have more people profiles than are included in your plan.`,
          text,
        };
      }
      if (objectProfilesExceeded) {
        let meteredRateObjectValue = getMeteredRateValue(billing.subscription?.plan.meteredObjectRate);
        let text =
          meteredRateObjectValue === 'enterprise'
            ? `Additional object profiles will have additional cost and will be added to your next bill, which we currently estimate to be ${billEstimate}.`
            : `Additional object profiles will be charged at $${meteredRateObjectValue} and will be added to your next bill, which we currently estimate to be ${billEstimate}.`;
        return {
          id: 'profiles',
          type: 'limit-exceeded',
          highlight: `You have more object profiles than are included in your plan.`,
          text,
        };
      }
    }
    return;
  }

  get cdpDescriptionItems() {
    let { cdpApiCallsAllotted } = this.usageMetrics;
    let { billing } = this;
    let cdpPremiumBasePrice = billing.subscription?.plan.cdpPremiumPrice;

    let cdpStarterPlanDetails = {
      planName: Plans.CdpStarter.name,
      basePrice: Plans.CdpStarter.basePrice,
      frequency: 'Free Forever',
    };

    let cdpPremiumPlanDetails = {
      planName: Plans.CdpPremium.name,
      basePrice: cdpPremiumBasePrice,
      frequency: `${capitalize(billing.subscription?.plan.intervalNameForDisplay ?? '')} billing`,
    };

    let planDetails =
      typeof cdpPremiumBasePrice === 'number' && cdpPremiumBasePrice > 0
        ? cdpPremiumPlanDetails
        : cdpStarterPlanDetails;

    return [
      {
        id: 'plan',
        title: 'Plan',
        value: planDetails.planName,
        detailsLink: {
          text: 'View plan benefits',
          url: 'https://customer.io/pricing/data-pipelines/',
        },
        width: 25,
      },
      {
        id: 'base-price',
        title: 'Base Price',
        value: formatCurrency([planDetails.basePrice], { precision: 0 }),
        details: planDetails.frequency,
        width: 20,
      },
      {
        id: 'included-api-calls',
        title: 'Included Api Calls',
        value: `${compactNumber(cdpApiCallsAllotted)} per month`,
        detailsLink: {
          text: "View add'l usage costs",
          url: 'https://customer.io/docs/accounts-and-workspaces/how-we-bill/#cdp-api-calls',
        },
        width: 25,
      },
    ];
  }

  get cdpUsageItems() {
    let { cdpApiCalls, cdpApiCallsAllotted } = this.usageMetrics;
    let { account } = this.args;
    return [
      {
        id: 'api-calls',
        title: 'Api Calls',
        details: `${formatNumber([cdpApiCalls])} of ${formatNumber([cdpApiCallsAllotted])}`,
        description: `Usage resets on ${this.moment.formatTimestamp([account.planPeriodEnd], {
          finalFormatter: (timestampAsMoment: moment.Moment) => timestampAsMoment.format('MMM D, YYYY'),
        })}`,
        percentage: getPercentage(cdpApiCalls, cdpApiCallsAllotted),
      },
    ];
  }

  get cdpBanner(): Banner | undefined {
    let { cdpApiCalls, cdpApiCallsAllotted, cdpApiCurrentTierRate } = this.usageMetrics;
    if (cdpApiCalls > cdpApiCallsAllotted) {
      return {
        id: 'api-calls',
        type: 'limit-exceeded',
        highlight: `You have used more API calls than are included in your plan.`,
        text: `Additional calls will be charged at $${
          cdpApiCurrentTierRate * 10000
        } per 10,000, and will be added to your next bill.`,
      };
    }
    return;
  }

  get canCancel() {
    let { billing } = this;
    return (
      billing.subscription?.plan.planType === Plans.Essentials.planType ||
      billing.subscription?.plan.planType === Plans.CdpStarter.planType ||
      billing.billingInfo?.cancellable === true
    );
  }

  get changePlanActionText() {
    return this.billing.billingInfo?.trial === true ? 'Upgrade Now' : 'Change Plan';
  }

  get isJourneysTrialOnPipelinesPlan() {
    return (
      this.account.planFeatures?.messaging?.enabled === true && this.account.planFeatures?.messaging?.expiresAt != null
    );
  }

  get isTrial() {
    return this.billing.billingInfo?.trial === true || this.isJourneysTrialOnPipelinesPlan === true;
  }

  get suspendTrialAt() {
    return this.isJourneysTrialOnPipelinesPlan
      ? this.account.planFeatures?.messaging?.expiresAt
      : this.args.account.suspendTrialAt;
  }

  get trialExpired() {
    return this.isJourneysTrialOnPipelinesPlan
      ? this.account.planFeatures?.messaging?.expired === true
      : this.billing.trialExpired === true;
  }

  get isBillingActive() {
    return this.billing.billingInfo?.active === true;
  }

  trackPlanEvent(
    eventName:
      | 'plan_action_clicked'
      | 'your_plan_available_plan_cta_clicked'
      | 'limits_banner_learn_more_clicked'
      | 'start_journeys_trial_clicked'
      | 'view_billing_help',
    data: Record<string, unknown> = {},
  ) {
    let { account } = this.args;
    let currentPlan = getCurrentPlan(this.billing.billingInfo, this.billing.subscription?.plan.planType);
    this.analytics.track('plan_billing', eventName, {
      data: { account_id: account.id, current_plan: currentPlan, ...data },
    });
  }

  @action
  handlePlanActionClick(action: 'upgrade' | 'change-plan' | 'cancel-modal') {
    this.trackPlanEvent('plan_action_clicked', { action });
    if (action !== 'cancel-modal') {
      if (this.billing.pinnedPlan != null) {
        this.router.transitionTo('settings.billing.checkout');
      } else {
        this.router.transitionTo('settings.billing.available-plans.journeys');
      }
    }
  }

  @action
  handleAvailablePlanActionClick(action: 'view-plans' | 'get-in-touch', availablePlan: string) {
    this.trackPlanEvent('your_plan_available_plan_cta_clicked', { action, available_plan: availablePlan });
    if (action === 'view-plans') {
      this.router.transitionTo('settings.billing.available-plans.journeys');
    }
  }

  @action
  trackLimitsBannerButtonClick(bannerId: BannerId) {
    this.trackPlanEvent('limits_banner_learn_more_clicked', { banner_id: bannerId });
  }

  @action
  trackStartJourneysTrialClick() {
    this.trackPlanEvent('start_journeys_trial_clicked');
  }

  get shouldDisplayBillingHelp() {
    let currentPlan = this.billing.subscription?.plan;

    let planSlug = currentPlan?.slug;

    return planSlug === BASIC_2018_PLAN_SLUG;
  }

  @action
  toggleBillingHelpSidebar() {
    this.sidebar.toggle(this.sidebarId);

    if (this.sidebar.activeSidebarId === this.sidebarId) {
      this.trackPlanEvent('view_billing_help', {});
    }
  }
}
